import React from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import { motion } from "framer-motion";

// LOGOS
import swanSolutionsLogo from "../../assets/logos/swansolutions_logo.png";
import figLogo from "../../assets/logos/fig_CROP.png";
import swanAiLabsLogo from "../../assets/logos/swan_ai_labs_logo.png";

// Partner/district logos (placeholder)
const partnerLogos = [
    figLogo,
    swanAiLabsLogo,
    swanSolutionsLogo,
];

export default function AboutUs() {
    return (
        <Box sx={{ bgcolor: "#f7fafb" }}>
            {/* HERO: full-width, gradient, logo, white text */}
            <Box
                component={motion.section}
                initial={{ opacity: 0, y: -64 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1 }}
                sx={{
                    width: "100vw",
                    left: "50%",
                    right: "50%",
                    marginLeft: "-50vw",
                    marginRight: "-50vw",
                    px: 0,
                    py: { xs: 10, md: 16 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    color: "#fff",
                    background: "linear-gradient(120deg, #0b223e 0%, #2269a6 45%, #21dbb8 100%)",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover",
                    minHeight: { xs: "40vh", md: "60vh" },
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: "0 6px 40px 0 rgba(30,90,150,0.13)",
                    mb: 8,
                }}
            >
                <Box
                    component="img"
                    src={swanSolutionsLogo}
                    alt="Swan Solutions Logo"
                    sx={{
                        height: { xs: 80, md: 140 },
                        mb: 2,
                        filter: "drop-shadow(0 4px 24px rgba(0,0,0,0.15))",
                    }}
                />
                <Typography
                    variant="h2"
                    sx={{
                        fontWeight: 900,
                        fontSize: { xs: "2.1rem", md: "3.2rem" },
                        color: "#fff",
                        mb: 2,
                        letterSpacing: "-0.04em",
                        textShadow: "0 3px 16px rgba(25,60,110,0.15)",
                    }}
                >
                    About Swan Solutions
                </Typography>
                <Typography
                    variant="h5"
                    sx={{
                        maxWidth: 680,
                        mx: "auto",
                        color: "rgba(255,255,255,0.93)",
                        fontWeight: 500,
                        fontSize: { xs: "1.1rem", md: "1.33rem" },
                        textShadow: "0 2px 14px rgba(20,92,140,0.13)",
                    }}
                >
                    Swan Solutions is a modern EdTech software development company founded in 2020, dedicated to building practical, enterprise-level systems for K12 school districts and the companies that serve them.
                </Typography>
            </Box>

            {/* Mission Statement & What We Do */}
            <Container maxWidth="md" sx={{ py: { xs: 7, md: 10 }, textAlign: "center" }}>
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                >
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 800,
                            color: "primary.main",
                            mb: 2,
                        }}
                    >
                        Mission & Focus
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            fontSize: { xs: "1.12rem", md: "1.22rem" },
                            color: "text.primary",
                            fontWeight: 400,
                            mb: 3,
                            maxWidth: 820,
                            mx: "auto",
                        }}
                    >
                        We partner with K12 school districts nationwide to design, build, and support robust technology solutions that power daily operations, ensure compliance, and streamline work for administrators and staff. While our core expertise lies in K12 nutrition, our work extends to every part of the education operations ecosystem - finance, procurement, workflow, records management, special diets, and more.
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            fontSize: { xs: "1.09rem", md: "1.16rem" },
                            color: "text.secondary",
                            mb: 3,
                            maxWidth: 820,
                            mx: "auto",
                        }}
                    >
                        Our systems are trusted by districts of all sizes - from small rural schools to some of the largest urban districts in the country - as well as by EdTech providers, food manufacturers, and service organizations supporting K12. We work side-by-side with leaders, adapting quickly to new regulations and the realities of school business, without layers of bureaucracy or endless delays.
                    </Typography>
                </motion.div>
            </Container>

            {/* What Makes Us Different */}
            <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
                <Grid container spacing={5}>
                    <Grid item xs={12} md={6}>
                        <motion.div
                            initial={{ opacity: 0, x: -40 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                                Practical Solutions, Built With You
                            </Typography>
                            <Typography variant="body1" sx={{ color: "text.secondary", mb: 2 }}>
                                We don't just deliver software - we deliver working solutions to real-world K12 challenges. Our subject matter experts work directly with nutrition, business, operations, and IT staff to ensure every system matches the day-to-day needs and regulatory environment of schools. We believe that successful technology starts with deep field experience and responsive support, not cookie-cutter products or one-size-fits-all models.
                            </Typography>
                            <Typography variant="body1" sx={{ color: "text.secondary" }}>
                                Our independence allows us to respond quickly, improve rapidly, and adapt to new challenges. We build, maintain, and continuously improve our products in direct partnership with the professionals who use them every day.
                            </Typography>
                        </motion.div>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <motion.div
                            initial={{ opacity: 0, x: 40 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <Box
                                component="img"
                                src={figLogo}
                                alt="FIG K12"
                                sx={{ height: 70, mb: 2, mr: 2 }}
                            />
                            <Box
                                component="img"
                                src={swanAiLabsLogo}
                                alt="Swan AI Labs"
                                sx={{ height: 70, mb: 2, ml: 2 }}
                            />
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    mt: 2,
                                    fontWeight: 600,
                                    color: "primary.main",
                                    mb: 1,
                                }}
                            >
                                Our Technology Powers:
                            </Typography>
                            <ul style={{ textAlign: "left", color: "#33577a", fontSize: "1.06rem", paddingLeft: 24 }}>
                                <li>Procurement & finance platforms for school nutrition</li>
                                <li>District-wide compliance & reporting tools</li>
                                <li>Special diet, workflow, & staff management solutions</li>
                                <li>Automated records & event management</li>
                                <li>API-driven integrations and enterprise applications for K12 and their partner companies</li>
                                <li>AI-powered analytics and process automation</li>
                            </ul>
                        </motion.div>
                    </Grid>
                </Grid>
            </Container>

            {/* Impact & Reach */}
            <Box sx={{ bgcolor: "#eaf5fa", py: { xs: 7, md: 10 }, textAlign: "center" }}>
                <motion.div
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                >
                    <Typography
                        variant="h4"
                        sx={{ fontWeight: 800, mb: 2, color: "primary.main" }}
                    >
                        National Impact
                    </Typography>
                    <Typography
                        sx={{
                            maxWidth: 800,
                            mx: "auto",
                            color: "text.primary",
                            fontSize: { xs: "1.1rem", md: "1.18rem" },
                            mb: 3,
                        }}
                    >
                        Today, Swan Solutions supports over 180 school districts in 27 states, ranging from small rural schools to some of the largest urban systems. Our platforms are used by thousands of K12 professionals, and our network includes nutrition directors, business administrators, EdTech partners, manufacturers, and agencies serving child nutrition.
                    </Typography>
                    <Grid container spacing={2} justifyContent="center" alignItems="center" sx={{ mt: 3 }}>
                        {partnerLogos.map((logo, i) => (
                            <Grid item key={i}>
                                <Box
                                    component="img"
                                    src={logo}
                                    alt={`Partner logo ${i + 1}`}
                                    sx={{ height: 48, mx: 2, my: 1, borderRadius: 2, bgcolor: "#fff", boxShadow: 2 }}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </motion.div>
            </Box>

            {/* Call To Action */}
            <Container maxWidth="md" sx={{ py: { xs: 8, md: 10 }, textAlign: "center" }}>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 800,
                            mb: 2,
                            color: "primary.main",
                            letterSpacing: "-0.01em",
                        }}
                    >
                        Ready to Modernize Your District or EdTech Offering?
                    </Typography>
                    <Typography
                        sx={{
                            maxWidth: 640,
                            mx: "auto",
                            mb: 3,
                            color: "text.secondary",
                            fontSize: "1.13rem",
                        }}
                    >
                        Whether you need district-wide operations platforms, custom nutrition solutions, or enterprise integrations that connect your services to K12 schools, Swan Solutions is ready to help.
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        href="/contact"
                        sx={{
                            borderRadius: 99,
                            fontWeight: 700,
                            fontSize: "1.15rem",
                            py: 1.5,
                            px: 5,
                            mt: 2,
                            background: "linear-gradient(90deg, #145c8c, #21dbb8)",
                        }}
                        component={motion.a}
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.96 }}
                    >
                        Contact Us
                    </Button>
                </motion.div>
            </Container>
        </Box>
    );
}
